/**
 * Compress an image File to max dimension (default 1024px) and JPEG quality (0.8).
 * Returns a File object. Falls back to original file if compression fails or file is not an image.
 */
export async function compressImage(file, maxSize = 1024, quality = 0.8) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // Skip compression if file is already small (< 250KB)
  if (file.size < 250 * 1024) return file;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUrl;
    });

    const { width, height } = img;
    let targetW = width;
    let targetH = height;
    if (Math.max(width, height) > maxSize) {
      if (width >= height) {
        targetW = maxSize;
        targetH = Math.round((height / width) * maxSize);
      } else {
        targetH = maxSize;
        targetW = Math.round((width / height) * maxSize);
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;

    // Keep original name but force .jpg extension
    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    const compressed = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
    // If compression actually made it bigger (rare), return original
    return compressed.size < file.size ? compressed : file;
  } catch (e) {
    console.warn('Image compression failed, using original:', e);
    return file;
  }
}
